// =============================================================================
// Association Types (from junction tables)
// =============================================================================

export interface InvoiceAssociation {
  id: string;
  invoiceNumber: string;
  status: string;
  invoiceDate: string | null;
  total: string;
}

export interface QuoteAssociation {
  id: string;
  quoteNumber: string;
  status: string;
  total: string;
}

// =============================================================================
// Inactive Sensor Status Types
// =============================================================================

/**
 * Inactive sensor status codes from MongoDB
 * 5 = Inactive
 */
export type InactiveSensorStatus = 5;

/** Array of all inactive status codes for API queries */
export const INACTIVE_STATUS_CODES: InactiveSensorStatus[] = [5];

/** Status configuration with labels and colors */
export const SENSOR_STATUS_MAP: Record<
  InactiveSensorStatus,
  { label: string; color: string; bgColor: string; textColor: string }
> = {
  5: {
    label: 'Inactive',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
  },
};

// =============================================================================
// Sensor Types
// =============================================================================

/** Organisation reference from populated sensor */
export interface SensorOrganisation {
  _id: string;
  name: string;
}

/** Site reference from populated sensor */
export interface SensorSite {
  _id: string;
  name: string;
}

/** Location reference from populated sensor */
export interface SensorLocation {
  _id: string;
  name: string;
}

/** Sensor type reference from populated sensor */
export interface SensorType {
  _id: string;
  name: string;
}

/** Stream reference from populated sensor */
export interface SensorStream {
  _id: string;
  name: string;
}

/** Inactive sensor with populated references */
export interface InactiveSensor {
  _id: string;
  name: string;
  status: InactiveSensorStatus;
  timestamp: string | null;
  billable: boolean;
  organisation: SensorOrganisation | null;
  site: SensorSite | null;
  location: SensorLocation | null;
  // These fields are not populated to reduce payload size
  sensortype?: SensorType | null;
  stream?: SensorStream | null;
  quoteNumbers: string[];
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface InactiveSensorsFilters {
  search?: string;
  status?: InactiveSensorStatus;
  organisation_id?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

// =============================================================================
// Response Types
// =============================================================================

export interface InactiveSensorsResponse {
  sensors: InactiveSensor[];
  total: number;
  page_number: number;
  page_length: number;
}

export interface InactiveSensorsSummary {
  total: number;
  inactive: number;
}

/**
 * Fleet-wide sensor counts (all statuses, not just inactive).
 * Note: active and inhibited overlap on status 8 (inhibited sensors are
 * part of the operational fleet). Cards don't sum to total.
 */
export interface FleetSummary {
  total: number;       // All sensors in the fleet
  active: number;      // Status 1 (Active), 0 (Unmanaged), 8 (Inhibited), 2 (Too Low), 3 (Too High)
  inactive: number;    // Status 5 only (truly offline)
  maintenance: number; // Status 6
  inhibited: number;   // Status 8
}

// =============================================================================
// Sensor Detail Types (V4 API)
// =============================================================================

/** Full sensor detail from V4 endpoint */
export interface SensorDetail {
  id: string;
  mongoSensorId: string;
  name: string;
  status: number;
  billable: boolean;
  lastReadingAt: string | null;
  firstDataAt: string | null;
  currentValue: string | null;
  sensorType: string | null;
  sensorTypeName: string | null;
  equipmentType: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  archived: boolean;
  archivedAt: string | null;
  maintenance: boolean;
  // Detail fields
  inputNumber: string | null;
  customId: string | null;
  description: string | null;
  tags: string[];
  invoices: string[];
  quotes: string[];
  subscriptions: string[];
  // New structured associations (from junction tables)
  invoiceAssociations?: InvoiceAssociation[];
  quoteAssociations?: QuoteAssociation[];
  streamName: string | null;
  usergroupName: string | null;
  // Stream/calibration fields (from streams JOIN)
  probeSerial: string | null;
  calibrationDue: string | null;
  calibrationCertNumber: string | null;
  transmitterName: string | null;
  // Relations
  account: { id: string; name: string; accountNumber: string } | null;
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** Organisation option for dropdown filter */
export interface OrganisationOption {
  id: string;
  name: string;
}
